import { useInfiniteQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdSettlementsSettlementIdSettlementEvents } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { SettlementEvent } from '~hooks/useSettlements/models';
import { extractPagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';

export default function useSettlementEventsInfiniteQuery({
  companyId,
  settlementId,
  settlementUpdatedAt,
}: {
  companyId: string;
  settlementId: string;
  settlementUpdatedAt: string;
}) {
  const query = useInfiniteQuery({
    queryKey: ['settlementEvents', companyId, settlementId, settlementUpdatedAt],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      try {
        const response =
          await getV1CompaniesCompanyIdSettlementsSettlementIdSettlementEvents({
            path: { 'company-id': companyId, 'settlement-id': settlementId },
            query: {
              'page[limit]': DEFAULT_API_PAGE_SIZE,
              ...(pageParam ? { 'page[after]': pageParam } : {}),
            },
            signal,
          });

        return {
          events: response.data.data.map(SettlementEvent.parse),
          nextCursor: extractPagination(response).after,
        };
      } catch (error) {
        reportRequestError(
          error,
          t('error_messages.settlements.failed_to_fetch_history'),
        );

        throw error;
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    retry: false,
  });

  const events = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.events),
    [query.data?.pages],
  );

  return {
    events,
    fetchMore: query.fetchNextPage,
    hasMore: query.hasNextPage,
    isError: query.isError,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    refetch: query.refetch,
  };
}
